/**
 * useDashboardData Hook
 * React Query hook for fetching and caching dashboard data
 */

import { useQuery } from "@tanstack/react-query";
import { fetchDashboardData } from "../modules/DashboardService";

// Query key for dashboard data
export const DASHBOARD_QUERY_KEY = "dashboard";

/**
 * Custom hook for fetching dashboard data with React Query
 *
 * @param {string} companyId - Company ID to fetch data for
 * @param {string} timezone - IANA timezone string (e.g., "America/Toronto")
 * @param {Object} options - Additional React Query options
 * @returns {Object} - React Query result object
 */
export function useDashboardData(companyId, timezone, options = {}) {
    return useQuery({
        queryKey: [DASHBOARD_QUERY_KEY, companyId, timezone],
        queryFn: () => fetchDashboardData(companyId, timezone),
        staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
        cacheTime: 30 * 60 * 1000, // 30 minutes - cache retention
        refetchOnWindowFocus: false, // Don't refetch on tab focus
        refetchOnMount: false, // Don't refetch if data exists
        retry: 2, // Retry failed requests twice
        enabled: Boolean(companyId), // Only fetch if companyId exists
        ...options
    });
}

/**
 * Hook configuration for auto-refresh (currently disabled)
 *
 * To enable auto-refresh, pass these options to useDashboardData:
 *
 * const { data } = useDashboardData(companyId, timezone, {
 *   refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
 *   refetchIntervalInBackground: false // Don't refresh when tab is hidden
 * });
 *
 * Alternative approaches for auto-refresh:
 *
 * 1. Polling (simple, reliable):
 *    refetchInterval: 300000 // 5 minutes
 *
 * 2. Visibility-based (efficient):
 *    refetchOnWindowFocus: true
 *    staleTime: 60000 // 1 minute
 *
 * 3. Time-based stale (balance of fresh/efficient):
 *    staleTime: 300000 // 5 minutes
 *    refetchOnMount: 'always'
 *
 * 4. WebSocket/Subscription (real-time, complex):
 *    Use Amplify subscriptions with queryClient.setQueryData()
 */

export default useDashboardData;
